/**
 * Evidence Extractor Layout Component
 *
 * Provides the main layout structure including:
 * - Shared AppHeader with user info and logout button
 * - Content area for Evidence Extractor pages
 * - Integrates with unified authentication system
 *
 * Theme Integration (Requirements 13.1, 13.2, 13.4):
 * - Uses the shared theme from frontend/shared/src/theme
 * - The ThemeProvider wraps the entire unified app in frontend/App.tsx
 * - CSS variables (--caa-cream, etc.) are used for consistent styling
 * - Layout is responsive for desktop and tablet viewports
 *
 * Requirements: 1.2, 1.3, 1.4, 13.1, 13.2, 13.3, 13.4
 * - 1.2: Authenticated users see their name or email displayed
 * - 1.3: Users can log out via a logout button
 * - 1.4: Session state is maintained across page navigation
 * - 13.1: Uses the shared theme from frontend/shared/src/theme
 * - 13.2: Uses Fluent UI components consistent with the Policy Navigator
 * - 13.3: Header matches Policy Navigator styling
 * - 13.4: Implements responsive design for desktop and tablet viewports
 */

import React, { ReactNode } from 'react';
import { AppHeader } from '../../../../shared/src/components';
import styles from './Layout.module.css';

export interface LayoutProps {
  /** Child components to render in the content area */
  children: ReactNode;
}

/**
 * Layout component for Evidence Extractor pages
 *
 * Features:
 * - Displays shared AppHeader with user name/email (Req 1.2)
 * - Provides logout button in header (Req 1.3)
 * - Maintains consistent layout across page navigation (Req 1.4)
 * - Uses shared header styling matching Policy Navigator (Req 13.3)
 */
export const Layout: React.FC<LayoutProps> = ({ children }) => {
  return (
    <div className={styles.layout}>
      <AppHeader currentApp="evidence_extractor" />
      <main className={styles.content}>
        {children}
      </main>
    </div>
  );
};

export default Layout;
